import React, { useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faPencilAlt, faSave } from '@fortawesome/free-solid-svg-icons';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Toast from './Toast';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  position: relative;
  max-height: 80vh;
  overflow-y: auto;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const Title = styled.h2`
  margin-bottom: 1.5rem;
  color: #316C6E;
`;

const Field = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #666;
  font-weight: bold;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 1rem;

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 1rem;
  min-height: 120px;
  font-family: inherit;
  font-size: inherit;
  line-height: 1.5;
  resize: vertical;

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 1rem;

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const SaveButton = styled.button`
  background-color: #316C6E;
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  float: right;
  
  &:hover {
    background-color: #245051;
  }
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;

`;

const EditButton = styled.button`
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  color: #666;
  
  &:hover {
    color: #333;
  }
`;

const TypeDurationContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const MapWrapper = styled.div`
  margin: 1rem 0;
  height: 200px;
  border-radius: 4px;
  overflow: hidden;
`;

const ViewModeContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const ViewModeSection = styled.div`
  margin-bottom: 1.5rem;
`;

const ViewModeLabel = styled.div`
  color: #666;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
`;

const ViewModeValue = styled.div`
  color: #333;
  font-size: 1rem;
  white-space: pre-line;
`;

const EditIcon = styled(FontAwesomeIcon)`
  font-size: 1rem;
  color: #666;
  &:hover {
    color: #333;
  }
`;

const CustomActivityDetailsModal = ({ activity, onClose, onUpdate, isReadOnly }) => {
  const { language } = useLanguage();
  const t = translations[language].customActivity;
  
  const [isEditing, setIsEditing] = useState(false);
  const [editedActivity, setEditedActivity] = useState({ ...activity });
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  const activityTypes = [
    { value: 'task', label: t.types.task },
    { value: 'hotel', label: t.types.hotel },
    { value: 'exploration', label: t.types.exploration },
    { value: 'rent_a_car', label: t.types.rent_a_car },
    { value: 'restaurant', label: t.types.restaurant },
    { value: 'poi', label: t.types.poi }
  ];

  const handleEdit = (e) => {
    e.stopPropagation();
    
    if (isReadOnly) {
      setToastMessage('This operation is only allowed for the owner of the trip, clone this trip if you want to make your own changes');
      setToastType('warning');
      setShowToast(true);
      return;
    }
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      await onUpdate(editedActivity);
      setToastMessage(t.updateSuccess);
      setToastType('success');
      setShowToast(true);
      setIsEditing(false);
    } catch (error) {
      setToastMessage(t.updateError);
      setToastType('error');
      setShowToast(true);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
        
        <TitleSection>
          <Title>{isEditing ? t.editTitle : t.activityDetails}</Title>
          {!isEditing && !isReadOnly && (
            <EditButton onClick={handleEdit}>
              <EditIcon icon={faPencilAlt} />
            </EditButton>
          )}
        </TitleSection>

        {isEditing ? (
          <>
            <Field>
              <Label>{t.name}</Label>
              <Input
                name="name"
                value={editedActivity.name}
                onChange={(e) => setEditedActivity({ ...editedActivity, name: e.target.value })}
              />
            </Field>

            <TypeDurationContainer>
              <Field style={{ flex: 2 }}>
                <Label>{t.type}</Label>
                <Select
                  name="type"
                  value={editedActivity.type}
                  onChange={(e) => setEditedActivity({ ...editedActivity, type: e.target.value })}
                >
                  {activityTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field style={{ flex: 1 }}>
                <Label>{t.duration}</Label>
                <Input
                  type="number"
                  name="duration"
                  value={editedActivity.duration}
                  onChange={(e) => setEditedActivity({ ...editedActivity, duration: e.target.value })}
                  min="1"
                />
              </Field>
            </TypeDurationContainer>

            <Field>
              <Label>{t.description}</Label>
              <TextArea
                name="description"
                value={editedActivity.description || ''}
                onChange={(e) => setEditedActivity({ ...editedActivity, description: e.target.value })}
              />
            </Field>

            <Field>
              <Label>
                <input
                  type="checkbox"
                  checked={editedActivity.hasLocation}
                  onChange={(e) => setEditedActivity({ 
                    ...editedActivity, 
                    hasLocation: e.target.checked,
                    lat: e.target.checked ? editedActivity.lat : null,
                    lng: e.target.checked ? editedActivity.lng : null
                  })}
                  style={{ marginRight: '8px' }}
                />
                {t.addLocation}
              </Label>
            </Field>

            <Field>
              <Label>{t.price}</Label>
              <Input
                type="number"
                name="price"
                value={editedActivity.price || ''}
                onChange={(e) => setEditedActivity({ ...editedActivity, price: e.target.value })}
                min="0"
                placeholder={t.optional}
              />
            </Field>

            {editedActivity.hasLocation && (
              <MapWrapper>
                <MapContainer
                  center={[editedActivity.lat || 18.7357, editedActivity.lng || -70.1627]}
                  zoom={8}
                  style={{ height: '100%', width: '100%' }}
                  whenCreated={(map) => {
                    map.on('click', (e) => {
                      setEditedActivity({
                        ...editedActivity,
                        lat: e.latlng.lat,
                        lng: e.latlng.lng
                      });
                    });
                  }}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  {editedActivity.lat && editedActivity.lng && (
                    <Marker
                      position={[editedActivity.lat, editedActivity.lng]}
                      icon={L.icon({
                        iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
                        iconSize: [25, 41],
                        iconAnchor: [12, 41],
                        popupAnchor: [1, -34],
                        shadowSize: [41, 41]
                      })}
                    />
                  )}
                </MapContainer>
              </MapWrapper>
            )}
          </>
        ) : (
          <>
            <ViewModeContainer>
              <ViewModeSection>
                <ViewModeLabel>{t.name}</ViewModeLabel>
                <ViewModeValue>{editedActivity.name}</ViewModeValue>
              </ViewModeSection>

              <ViewModeSection>
                <div style={{ display: 'flex', gap: '2rem' }}>
                  <div>
                    <ViewModeLabel>{t.type}</ViewModeLabel>
                    <ViewModeValue>
                      {t.types[editedActivity.type] || editedActivity.type}
                    </ViewModeValue>
                  </div>
                  <div>
                    <ViewModeLabel>{t.duration}</ViewModeLabel>
                    <ViewModeValue>{editedActivity.duration} {t.hours}</ViewModeValue>
                  </div>
                </div>
              </ViewModeSection>

              {editedActivity.description && (
                <ViewModeSection>
                  <ViewModeLabel>{t.description}</ViewModeLabel>
                  <ViewModeValue>{editedActivity.description}</ViewModeValue>
                </ViewModeSection>
              )}

              {editedActivity.lat && editedActivity.lng && (
                <ViewModeSection>
                  <ViewModeLabel>{t.location}</ViewModeLabel>
                  <MapWrapper>
                    <MapContainer
                      center={[editedActivity.lat, editedActivity.lng]}
                      zoom={8}
                      style={{ height: '100%', width: '100%' }}
                    >
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                      <Marker
                        position={[editedActivity.lat, editedActivity.lng]}
                        icon={L.icon({
                          iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
                          iconSize: [25, 41],
                          iconAnchor: [12, 41],
                          popupAnchor: [1, -34],
                          shadowSize: [41, 41]
                        })}
                      />
                    </MapContainer>
                  </MapWrapper>
                </ViewModeSection>
              )}

              {editedActivity.price && (
                <ViewModeSection>
                  <ViewModeLabel>{t.price}</ViewModeLabel>
                  <ViewModeValue>${editedActivity.price}</ViewModeValue>
                </ViewModeSection>
              )}
            </ViewModeContainer>
          </>
        )}

        {isEditing && (
          <SaveButton onClick={handleSave}>
            <FontAwesomeIcon icon={faSave} style={{ marginRight: '0.5rem' }} />
            {t.saveChanges}
          </SaveButton>
        )}

        {showToast && (
          <Toast
            message={toastMessage}
            onClose={() => setShowToast(false)}
            type={toastType}
          />
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default CustomActivityDetailsModal; 